import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import Cookies from 'js-cookie';
import axios from 'axios';
import { format } from 'date-fns';
import { Loader2, ChevronLeft, ChevronRight, ChevronsLeft, ChevronsRight, History, Search, CalendarIcon } from 'lucide-react';
import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Card } from '@/components/ui/card';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Calendar } from '@/components/ui/calendar';
import {
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableHeader,
    TableRow,
} from '@/components/ui/table';
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from '@/components/ui/select';
import MobileRestriction from '../../../components/MobileRestriction';
import { BACKEND_API_URL } from '../../../utils/api';

// Reusable component for truncated text with conditional tooltip
const TruncatedCell = ({ children, className }) => {
    const [isTruncated, setIsTruncated] = useState(false);
    const textRef = useRef(null);

    const checkTruncation = useCallback(() => {
        if (textRef.current) {
            setIsTruncated(textRef.current.scrollWidth > textRef.current.clientWidth);
        }
    }, []);

    useEffect(() => {
        checkTruncation();
        window.addEventListener('resize', checkTruncation);
        return () => window.removeEventListener('resize', checkTruncation);
    }, [checkTruncation, children]);

    const content = (
        <span
            ref={textRef}
            className={cn('truncate block w-full cursor-default', className)}
        >
            {children}
        </span>
    );

    if (!isTruncated) return content;

    return (
        <Tooltip>
            <TooltipTrigger asChild>{content}</TooltipTrigger>
            <TooltipContent side="top" className="max-w-[400px] break-all">
                {children}
            </TooltipContent>
        </Tooltip>
    );
};

const VisualChangelog = () => {
    const [searchParams, setSearchParams] = useSearchParams();

    // Local state for filters
    const [startDate, setStartDate] = useState(null);
    const [endDate, setEndDate] = useState(null);
    const [page, setPage] = useState(1);
    const [pageSize, setPageSize] = useState(25);

    // UI state
    const [startOpen, setStartOpen] = useState(false);
    const [endOpen, setEndOpen] = useState(false);
    const [movements, setMovements] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [totalCount, setTotalCount] = useState(0);
    const [totalPages, setTotalPages] = useState(0);
    const [hasSearched, setHasSearched] = useState(false);
    const [selectedRowId, setSelectedRowId] = useState(null);

    const quickFilters = [
        { label: 'Today', value: 'today', days: 0 },
        { label: '7 days', value: '7days', days: 7 },
        { label: '30 days', value: '30days', days: 30 },
        { label: '3 months', value: '3months', days: 90 },
        { label: '6 months', value: '6months', days: 180 },
        { label: '1 year', value: '12months', days: 365 },
        { label: '3 years', value: '3years', days: 1095 },
    ];

    // Check if local state matches URL (to enable/disable search button)
    const isUrlInSync = () => {
        const urlStart = searchParams.get('start');
        const urlEnd = searchParams.get('end');

        const localStart = startDate ? format(startDate, 'yyyy-MM-dd') : null;
        const localEnd = endDate ? format(endDate, 'yyyy-MM-dd') : null;

        return urlStart === localStart && urlEnd === localEnd;
    };

    // Check which quick filter matches current dates
    const getActiveFilter = () => {
        if (!startDate || !endDate) return null;
        const today = new Date();
        today.setHours(0, 0, 0, 0);

        const endNorm = new Date(endDate);
        endNorm.setHours(0, 0, 0, 0);
        if (endNorm.toDateString() !== today.toDateString()) return null;

        const startNorm = new Date(startDate);
        startNorm.setHours(0, 0, 0, 0);
        const diffDays = Math.round((today - startNorm) / (1000 * 60 * 60 * 24));

        for (const filter of quickFilters) {
            if (filter.value === 'today' && diffDays === 0) return filter.value;
            if (filter.days === diffDays) return filter.value;
        }
        return null;
    };

    // Apply quick filter - just sets dates
    const applyQuickFilter = (filter) => {
        const end = new Date();
        const start = new Date();
        if (filter.value === 'today') {
            start.setHours(0, 0, 0, 0);
        } else {
            start.setDate(start.getDate() - filter.days);
        }
        setStartDate(start);
        setEndDate(end);
    };

    // Fetch data from API
    const fetchMovements = async (start, end, pg, pgSize) => {
        if (!start || !end) {
            setError('Please select both start and end dates');
            return;
        }

        setLoading(true);
        setError(null);

        try {
            const accessToken = Cookies.get('access_token');
            const response = await axios.get(`${BACKEND_API_URL}/api/folder_movements/`, {
                params: {
                    start_date: format(start, 'yyyy-MM-dd'),
                    end_date: format(end, 'yyyy-MM-dd'),
                    page: pg,
                    page_size: pgSize,
                },
                headers: { Authorization: `Token ${accessToken}` },
            });
            setMovements(response.data.movements || []);
            setTotalCount(response.data.total_count || 0);
            setTotalPages(response.data.total_pages || 0);
            setHasSearched(true);
        } catch (err) {
            console.error('Error fetching movements:', err);
            setError(err.response?.data?.error || 'Failed to fetch folder movements');
        } finally {
            setLoading(false);
        }
    };

    // Update URL with current filters
    const updateUrl = (start, end, pg, pgSize) => {
        setSearchParams({
            start: format(start, 'yyyy-MM-dd'),
            end: format(end, 'yyyy-MM-dd'),
            page: String(pg),
            pageSize: String(pgSize),
        }, { replace: true });
    };

    // Search button - update URL and fetch
    const handleSearch = () => {
        if (!startDate || !endDate) {
            setError('Please select both start and end dates');
            return;
        }
        const newPage = 1;
        setPage(newPage);
        updateUrl(startDate, endDate, newPage, pageSize);
        fetchMovements(startDate, endDate, newPage, pageSize);
    };

    // Page change - update URL and fetch
    const handlePageChange = (newPage) => {
        setPage(newPage);
        updateUrl(startDate, endDate, newPage, pageSize);
        fetchMovements(startDate, endDate, newPage, pageSize);
    };

    // Page size change - update URL and fetch
    const handlePageSizeChange = (value) => {
        const newPageSize = parseInt(value);
        setPageSize(newPageSize);
        setPage(1);
        updateUrl(startDate, endDate, 1, newPageSize);
        fetchMovements(startDate, endDate, 1, newPageSize);
    };

    // On mount: read URL or set defaults
    useEffect(() => {
        const urlStart = searchParams.get('start');
        const urlEnd = searchParams.get('end');
        const urlPage = parseInt(searchParams.get('page')) || 1;
        const urlPageSize = parseInt(searchParams.get('pageSize')) || 25;

        if (urlStart && urlEnd) {
            // URL has params - restore state and fetch
            const start = new Date(urlStart);
            const end = new Date(urlEnd);
            setStartDate(start);
            setEndDate(end);
            setPage(urlPage);
            setPageSize(urlPageSize);
            fetchMovements(start, end, urlPage, urlPageSize);
        } else {
            // No URL params - set default 7 days
            const defaultFilter = quickFilters.find((f) => f.value === '7days');
            if (defaultFilter) {
                applyQuickFilter(defaultFilter);
            }
        }
    }, []); // Only on mount

    const formatDateDisplay = (dateString) => {
        try {
            return format(new Date(dateString), 'MMM dd HH:mm');
        } catch {
            return dateString;
        }
    };

    const formatDateFull = (dateString) => {
        try {
            return format(new Date(dateString), 'MMM dd, yyyy HH:mm:ss');
        } catch {
            return dateString;
        }
    };

    const getItemTypeVariant = (itemType) => {
        switch (itemType) {
            case 'folder': return 'default';
            case 'link': return 'secondary';
            case 'shortcut': return 'outline';
            default: return 'secondary';
        }
    };

    // Mobile portrait mode check
    if (window?.innerWidth <= 600) {
        return <MobileRestriction />;
    }

    const PaginationControls = () => (
        <div className="flex items-center gap-0.5">
            <Button
                variant="outline"
                size="icon-sm"
                className="h-6 w-6"
                onClick={() => handlePageChange(1)}
                disabled={page === 1 || loading}
            >
                <ChevronsLeft className="h-3 w-3" />
            </Button>
            <Button
                variant="outline"
                size="icon-sm"
                className="h-6 w-6"
                onClick={() => handlePageChange(page - 1)}
                disabled={page === 1 || loading}
            >
                <ChevronLeft className="h-3 w-3" />
            </Button>
            <span className="text-xs px-2 min-w-[60px] text-center tabular-nums">
                {page} / {totalPages}
            </span>
            <Button
                variant="outline"
                size="icon-sm"
                className="h-6 w-6"
                onClick={() => handlePageChange(page + 1)}
                disabled={page === totalPages || loading}
            >
                <ChevronRight className="h-3 w-3" />
            </Button>
            <Button
                variant="outline"
                size="icon-sm"
                className="h-6 w-6"
                onClick={() => handlePageChange(totalPages)}
                disabled={page === totalPages || loading}
            >
                <ChevronsRight className="h-3 w-3" />
            </Button>
        </div>
    );

    const activeFilter = getActiveFilter();

    return (
        <div className="h-[calc(100vh-96px)] flex flex-col bg-background overflow-hidden">
            {/* Loading overlay */}
            {loading && (
                <div className="absolute inset-0 z-50 flex items-center justify-center bg-background/80 backdrop-blur-sm">
                    <div className="flex flex-col items-center gap-3">
                        <Loader2 className="h-10 w-10 animate-spin text-primary" />
                        <span className="text-sm text-muted-foreground">Loading movements...</span>
                    </div>
                </div>
            )}

            {/* Filters Section */}
            <div className="shrink-0 border-b bg-card/50">
                <div className="flex items-center justify-between gap-4 px-4 py-2">
                    {/* Left: Title and quick filters */}
                    <div className="flex items-center gap-4">
                        <div className="flex items-center gap-2">
                            <History className="h-4 w-4 text-primary" />
                            <h1 className="text-sm font-semibold">Visual Changelog</h1>
                        </div>
                        <div className="hidden sm:flex items-center gap-1">
                            {quickFilters.map((filter) => (
                                <Button
                                    key={filter.value}
                                    variant={activeFilter === filter.value ? 'default' : 'ghost'}
                                    size="sm"
                                    className="h-7 px-2 text-xs"
                                    onClick={() => applyQuickFilter(filter)}
                                >
                                    {filter.label}
                                </Button>
                            ))}
                        </div>
                    </div>

                    {/* Right: Date pickers and search */}
                    <div className="flex items-center gap-2">
                        <div className="flex items-center gap-1.5">
                            <Popover open={startOpen} onOpenChange={setStartOpen}>
                                <PopoverTrigger asChild>
                                    <Button
                                        variant="outline"
                                        className={cn(
                                            "h-7 w-[130px] justify-start text-xs font-normal gap-1.5",
                                            !startDate && "text-muted-foreground"
                                        )}
                                    >
                                        <CalendarIcon className="h-3 w-3" />
                                        {startDate ? format(startDate, 'MMM dd, yyyy') : 'Start date'}
                                    </Button>
                                </PopoverTrigger>
                                <PopoverContent className="w-auto p-0" align="start">
                                    <Calendar
                                        mode="single"
                                        selected={startDate}
                                        captionLayout="dropdown"
                                        fromYear={2020}
                                        toYear={new Date().getFullYear()}
                                        onSelect={(date) => {
                                            setStartDate(date);
                                            setStartOpen(false);
                                        }}
                                        disabled={(date) => endDate && date > endDate}
                                        initialFocus
                                    />
                                </PopoverContent>
                            </Popover>
                            <span className="text-xs text-muted-foreground">to</span>
                            <Popover open={endOpen} onOpenChange={setEndOpen}>
                                <PopoverTrigger asChild>
                                    <Button
                                        variant="outline"
                                        className={cn(
                                            "h-7 w-[130px] justify-start text-xs font-normal gap-1.5",
                                            !endDate && "text-muted-foreground"
                                        )}
                                    >
                                        <CalendarIcon className="h-3 w-3" />
                                        {endDate ? format(endDate, 'MMM dd, yyyy') : 'End date'}
                                    </Button>
                                </PopoverTrigger>
                                <PopoverContent className="w-auto p-0" align="end">
                                    <Calendar
                                        mode="single"
                                        selected={endDate}
                                        captionLayout="dropdown"
                                        fromYear={2020}
                                        toYear={new Date().getFullYear()}
                                        onSelect={(date) => {
                                            setEndDate(date);
                                            setEndOpen(false);
                                        }}
                                        disabled={(date) => startDate && date < startDate}
                                        initialFocus
                                    />
                                </PopoverContent>
                            </Popover>
                        </div>
                        <Button
                            onClick={handleSearch}
                            disabled={loading || !startDate || !endDate || isUrlInSync()}
                            size="sm"
                            className="h-7 px-3 gap-1.5"
                        >
                            <Search className="h-3 w-3" />
                            Search
                        </Button>
                    </div>
                </div>

                {/* Error banner */}
                {error && (
                    <div className="flex items-center px-4 py-1.5 bg-destructive/10 border-t border-destructive/20 text-destructive text-xs">
                        {error}
                    </div>
                )}
            </div>

            {/* Results Section */}
            {!hasSearched ? (
                <div className="flex-1 flex items-center justify-center">
                    <Card className="p-8 text-center max-w-md border-dashed">
                        <History className="h-16 w-16 mx-auto mb-4 text-muted-foreground/30" />
                        <h3 className="text-lg font-semibold mb-2">Track Folder Movements</h3>
                        <p className="text-sm text-muted-foreground mb-4">
                            View the history of folder and item movements within your workspace.
                            Select a date range and click Search to see all changes.
                        </p>
                        <div className="flex flex-wrap justify-center gap-2 text-xs text-muted-foreground">
                            <Badge variant="outline">Folder moves</Badge>
                            <Badge variant="outline">Link relocations</Badge>
                            <Badge variant="outline">Shortcut changes</Badge>
                            <Badge variant="outline">User tracking</Badge>
                        </div>
                    </Card>
                </div>
            ) : movements.length > 0 ? (
                <div className="flex-1 flex flex-col min-h-0 p-4">
                    {/* Controls header */}
                    <div className="shrink-0 flex items-center justify-between gap-2 px-4 py-2 bg-muted/30 border border-border rounded-t-lg">
                        <span className="text-sm font-medium">
                            {totalCount} movement{totalCount !== 1 ? 's' : ''} found
                        </span>

                        <div className="flex items-center gap-3">
                            {totalPages > 1 && <PaginationControls />}

                            <Select value={String(pageSize)} onValueChange={handlePageSizeChange}>
                                <SelectTrigger className="h-7 w-[80px] text-xs">
                                    <SelectValue />
                                </SelectTrigger>
                                <SelectContent>
                                    {[10, 25, 50, 100].map((size) => (
                                        <SelectItem key={size} value={String(size)} className="text-xs">
                                            {size} / page
                                        </SelectItem>
                                    ))}
                                </SelectContent>
                            </Select>
                        </div>
                    </div>

                    {/* Table */}
                    <div className="flex-1 flex flex-col min-h-0 overflow-hidden border-x border-border">
                        <div className="shrink-0 bg-muted/50 border-b border-border">
                            <Table className="table-fixed w-full">
                                <TableHeader>
                                    <TableRow className="hover:bg-transparent">
                                        <TableHead className="w-[100px] text-[10px] uppercase tracking-wider font-semibold">Time</TableHead>
                                        <TableHead className="w-[25%] text-[10px] uppercase tracking-wider font-semibold">Item</TableHead>
                                        <TableHead className="w-[25%] text-[10px] uppercase tracking-wider font-semibold">From</TableHead>
                                        <TableHead className="w-[25%] text-[10px] uppercase tracking-wider font-semibold">To</TableHead>
                                        <TableHead className="w-[100px] text-[10px] uppercase tracking-wider font-semibold">Moved By</TableHead>
                                    </TableRow>
                                </TableHeader>
                            </Table>
                        </div>

                        <div className="flex-1 overflow-y-auto min-h-0">
                            <Table className="table-fixed w-full">
                                <TableBody>
                                    {movements.map((movement, index) => (
                                        <TableRow
                                            key={movement.id || index}
                                            className={cn(
                                                'cursor-pointer transition-colors border-l-4',
                                                selectedRowId === (movement.id || index)
                                                    ? 'bg-primary/15 border-l-primary hover:bg-primary/20'
                                                    : index % 2 === 0
                                                        ? 'bg-background border-l-transparent hover:bg-primary/5'
                                                        : 'bg-muted/20 border-l-transparent hover:bg-primary/8'
                                            )}
                                            onClick={() => setSelectedRowId(movement.id || index)}
                                        >
                                            <TableCell className="w-[100px] text-muted-foreground py-2 overflow-hidden">
                                                <Tooltip>
                                                    <TooltipTrigger asChild>
                                                        <span className="cursor-default text-xs truncate block">
                                                            {formatDateDisplay(movement.timestamp)}
                                                        </span>
                                                    </TooltipTrigger>
                                                    <TooltipContent side="top">
                                                        {formatDateFull(movement.timestamp)}
                                                    </TooltipContent>
                                                </Tooltip>
                                            </TableCell>

                                            <TableCell className="w-[25%] py-2 overflow-hidden">
                                                <div className="flex items-center gap-2 min-w-0">
                                                    <Badge
                                                        variant={getItemTypeVariant(movement.item_type)}
                                                        className="h-5 text-[10px] px-2 py-0 shrink-0"
                                                    >
                                                        {movement.item_type}
                                                    </Badge>
                                                    <TruncatedCell className="font-medium text-xs min-w-0 flex-1 text-foreground">
                                                        {movement.item_name}
                                                    </TruncatedCell>
                                                    <span className="text-muted-foreground text-[10px] shrink-0">
                                                        ({movement.item_id})
                                                    </span>
                                                </div>
                                            </TableCell>

                                            <TableCell className="w-[25%] py-2 font-mono text-[11px] text-muted-foreground overflow-hidden">
                                                <TruncatedCell>{movement.old_parent_path || 'Root'}</TruncatedCell>
                                            </TableCell>

                                            <TableCell className="w-[25%] py-2 font-mono text-[11px] text-muted-foreground overflow-hidden">
                                                <TruncatedCell>{movement.new_parent_path || 'Root'}</TruncatedCell>
                                            </TableCell>

                                            <TableCell className="w-[100px] py-2 text-muted-foreground overflow-hidden">
                                                <TruncatedCell className="text-xs">{movement.moved_by || 'Unknown'}</TruncatedCell>
                                            </TableCell>
                                        </TableRow>
                                    ))}
                                </TableBody>
                            </Table>
                        </div>
                    </div>

                    {/* Footer */}
                    <div className="shrink-0 flex items-center justify-between gap-2 px-4 py-2 bg-muted/30 border border-border rounded-b-lg text-xs text-muted-foreground">
                        <span className="tabular-nums">
                            Showing {(page - 1) * pageSize + 1}–{Math.min(page * pageSize, totalCount)} of {totalCount}
                        </span>
                        {totalPages > 1 && <PaginationControls />}
                    </div>
                </div>
            ) : (
                <div className="flex-1 flex items-center justify-center">
                    <Card className="p-8 text-center max-w-md border-dashed">
                        <History className="h-16 w-16 mx-auto mb-4 text-muted-foreground/30" />
                        <h3 className="text-lg font-semibold mb-2">No Movements Found</h3>
                        <p className="text-sm text-muted-foreground">
                            No folder movements were recorded in the selected date range.
                            Try expanding your search to a longer time period.
                        </p>
                    </Card>
                </div>
            )}
        </div>
    );
};

export default VisualChangelog;
